<template>
  <div class="ccs-editor">
    <div
      v-for="(section, idx) in sections"
      :key="section._key"
      class="ccs-card"
      :class="{ 'ccs-drag-over': dragOverIdx === idx }"
      draggable="true"
      @dragstart="onDragStart(idx, $event)"
      @dragover.prevent="onDragOver(idx)"
      @drop.prevent="onDrop(idx)"
      @dragend="onDragEnd"
    >
      <div class="ccs-card-inner">
        <span class="ccs-handle" title="Arrastar para reordenar">⠿</span>
        <div class="ccs-fields">
          <input
            type="text"
            class="ccs-title-input"
            placeholder="Título da secção"
            :value="section.title"
            @input="updateTitle(idx, $event.target.value)"
          />
          <div class="ccs-rte-wrap">
            <div class="ccs-toolbar">
              <button type="button" :class="{ active: editors[idx] && editors[idx].isActive('bold') }" @click="editors[idx] && editors[idx].chain().focus().toggleBold().run()"><b>B</b></button>
              <button type="button" :class="{ active: editors[idx] && editors[idx].isActive('italic') }" @click="editors[idx] && editors[idx].chain().focus().toggleItalic().run()"><i>I</i></button>
              <button type="button" :class="{ active: editors[idx] && editors[idx].isActive('heading', { level: 2 }) }" @click="editors[idx] && editors[idx].chain().focus().toggleHeading({ level: 2 }).run()">H2</button>
              <button type="button" :class="{ active: editors[idx] && editors[idx].isActive('heading', { level: 3 }) }" @click="editors[idx] && editors[idx].chain().focus().toggleHeading({ level: 3 }).run()">H3</button>
              <button type="button" :class="{ active: editors[idx] && editors[idx].isActive('bulletList') }" @click="editors[idx] && editors[idx].chain().focus().toggleBulletList().run()">UL</button>
              <button type="button" :class="{ active: editors[idx] && editors[idx].isActive('orderedList') }" @click="editors[idx] && editors[idx].chain().focus().toggleOrderedList().run()">OL</button>
              <button type="button" @click="insertLink(idx)">Link</button>
              <button type="button" @click="triggerImageUpload(idx)">Image</button>
            </div>
            <editor-content :editor="editors[idx]" class="ccs-tiptap-content" />
          </div>
        </div>
        <button type="button" class="ccs-remove-btn" @click="removeSection(idx)">Remover</button>
      </div>
    </div>

    <button type="button" class="ccs-add-btn" @click="addSection">+ Adicionar secção</button>

    <!-- Hidden file input for image upload, one per potential section -->
    <input
      ref="imageFileInput"
      type="file"
      accept="image/*"
      style="display:none"
      @change="onImageFileSelected"
    />
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import axios from "axios";
import { flipmemoApiBase } from "@/config/flipmemoApi";

let _keyCounter = 0;
function nextKey() {
  return ++_keyCounter;
}

export default {
  name: "CityCustomSectionsEditor",

  components: { EditorContent },

  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
  },

  emits: ["update:modelValue"],

  data() {
    return {
      sections: [],
      editors: [],
      dragSrcIdx: null,
      dragOverIdx: null,
      pendingImageEditorIdx: null,
    };
  },

  watch: {
    modelValue: {
      immediate: true,
      deep: true,
      handler(newVal) {
        const incoming = Array.isArray(newVal) ? newVal : [];
        // Only re-sync if the number of sections changed or IDs differ
        // to avoid destroying editors on every keystroke
        const same =
          incoming.length === this.sections.length &&
          incoming.every((s, i) => this.sections[i] && this.sections[i].id === s.id);
        if (same) return;
        this.destroyEditors();
        this.sections = incoming.map((s) => ({ ...s, _key: nextKey() }));
        this.$nextTick(() => this.buildEditors());
      },
    },
  },

  beforeUnmount() {
    this.destroyEditors();
  },

  methods: {
    buildEditors() {
      this.editors = this.sections.map((section, idx) => {
        const editor = new Editor({
          extensions: [
            StarterKit,
            Link.configure({ openOnClick: false }),
            Image,
          ],
          content: section.contentHtml || "",
          onUpdate: ({ editor }) => {
            this.sections[idx].contentHtml = editor.getHTML();
            this.emitChange();
          },
        });
        return editor;
      });
    },

    destroyEditors() {
      (this.editors || []).forEach((e) => e && e.destroy());
      this.editors = [];
    },

    emitChange() {
      this.$emit(
        "update:modelValue",
        this.sections.map((s) => ({
          id: s.id || 0,
          title: s.title || "",
          contentHtml: s.contentHtml || "",
          anchorSlug: s.anchorSlug || "",
          order: s.order || 0,
        })),
      );
    },

    updateTitle(idx, value) {
      this.sections[idx].title = value;
      this.emitChange();
    },

    addSection() {
      const newSection = {
        id: 0,
        title: "",
        contentHtml: "",
        anchorSlug: "",
        order: 0,
        _key: nextKey(),
      };
      this.sections.push(newSection);

      this.$nextTick(() => {
        const editor = new Editor({
          extensions: [
            StarterKit,
            Link.configure({ openOnClick: false }),
            Image,
          ],
          content: "",
          onUpdate: ({ editor }) => {
            const i = this.sections.indexOf(newSection);
            if (i >= 0) {
              this.sections[i].contentHtml = editor.getHTML();
              this.emitChange();
            }
          },
        });
        this.editors.push(editor);
        this.emitChange();
      });
    },

    removeSection(idx) {
      if (!window.confirm("Remover esta secção?")) return;
      if (this.editors[idx]) {
        this.editors[idx].destroy();
      }
      this.editors.splice(idx, 1);
      this.sections.splice(idx, 1);
      // Re-wire onUpdate callbacks for shifted editors
      this.editors.forEach((editor, i) => {
        if (!editor) return;
        const sec = this.sections[i];
        editor.off("update");
        editor.on("update", ({ editor: ed }) => {
          sec.contentHtml = ed.getHTML();
          this.emitChange();
        });
      });
      this.emitChange();
    },

    insertLink(idx) {
      const editor = this.editors[idx];
      if (!editor) return;
      const url = window.prompt("URL do link:");
      if (!url) return;
      editor.chain().focus().setLink({ href: url }).run();
    },

    triggerImageUpload(idx) {
      this.pendingImageEditorIdx = idx;
      this.$refs.imageFileInput.value = "";
      this.$refs.imageFileInput.click();
    },

    async onImageFileSelected(event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      const idx = this.pendingImageEditorIdx;
      const editor = idx != null ? this.editors[idx] : null;
      if (!editor) return;

      const token = localStorage.getItem("token");
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(
          `${flipmemoApiBase}/uploads?folder=section`,
          fd,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const url = res.data?.url;
        if (url) {
          editor.chain().focus().setImage({ src: url }).run();
        }
      } catch (err) {
        console.error("Image upload failed:", err);
        alert("Falha ao enviar imagem. Verifique o console.");
      }
    },

    // Drag-and-drop reordering
    onDragStart(idx, event) {
      this.dragSrcIdx = idx;
      event.dataTransfer.effectAllowed = "move";
    },

    onDragOver(idx) {
      this.dragOverIdx = idx;
    },

    onDrop(targetIdx) {
      const srcIdx = this.dragSrcIdx;
      if (srcIdx == null || srcIdx === targetIdx) return;

      // Move section
      const [movedSection] = this.sections.splice(srcIdx, 1);
      this.sections.splice(targetIdx, 0, movedSection);

      // Move editor
      const [movedEditor] = this.editors.splice(srcIdx, 1);
      this.editors.splice(targetIdx, 0, movedEditor);

      // Re-wire onUpdate callbacks after reorder
      this.editors.forEach((editor, i) => {
        if (!editor) return;
        const sec = this.sections[i];
        editor.off("update");
        editor.on("update", ({ editor: ed }) => {
          sec.contentHtml = ed.getHTML();
          this.emitChange();
        });
      });

      this.emitChange();
    },

    onDragEnd() {
      this.dragSrcIdx = null;
      this.dragOverIdx = null;
    },
  },
};
</script>

<style scoped>
.ccs-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ccs-card {
  background: #0f1c2e;
  border: 1px solid #1e3352;
  border-radius: 10px;
  padding: 12px;
  transition: border-color 0.15s;
}

.ccs-card.ccs-drag-over {
  border-color: #4f8ef7;
  background: #112236;
}

.ccs-card-inner {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.ccs-handle {
  font-size: 18px;
  color: #4a6080;
  cursor: grab;
  user-select: none;
  padding-top: 10px;
  flex-shrink: 0;
}

.ccs-handle:active {
  cursor: grabbing;
}

.ccs-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.ccs-title-input {
  border: 1px solid #1e3352;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  background: #081526;
  color: #e2eaf4;
  width: 100%;
  box-sizing: border-box;
}

.ccs-title-input::placeholder {
  color: #4a6080;
}

.ccs-rte-wrap {
  border: 1px solid #1e3352;
  border-radius: 8px;
  overflow: hidden;
  background: #081526;
}

.ccs-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  padding: 6px 8px;
  border-bottom: 1px solid #1e3352;
  background: #0d1b2e;
}

.ccs-toolbar button {
  border: 1px solid #1e3352;
  background: transparent;
  color: #94adc8;
  border-radius: 5px;
  padding: 3px 7px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.1s, color 0.1s;
}

.ccs-toolbar button:hover {
  background: #1e3352;
  color: #e2eaf4;
}

.ccs-toolbar button.active {
  background: #1e3a8a;
  color: #93c5fd;
  border-color: #3b5fcf;
}

.ccs-tiptap-content {
  padding: 10px 12px;
  min-height: 80px;
  color: #e2eaf4;
  font-size: 14px;
  line-height: 1.55;
}

/* TipTap ProseMirror reset */
.ccs-tiptap-content :deep(.ProseMirror) {
  outline: none;
  min-height: 60px;
}

.ccs-tiptap-content :deep(.ProseMirror p) {
  margin: 0 0 6px;
}

.ccs-tiptap-content :deep(.ProseMirror h2) {
  font-size: 1.15em;
  font-weight: 700;
  margin: 10px 0 4px;
  color: #c6d9f7;
}

.ccs-tiptap-content :deep(.ProseMirror h3) {
  font-size: 1em;
  font-weight: 700;
  margin: 8px 0 4px;
  color: #c6d9f7;
}

.ccs-tiptap-content :deep(.ProseMirror ul),
.ccs-tiptap-content :deep(.ProseMirror ol) {
  padding-left: 20px;
  margin: 4px 0 6px;
}

.ccs-tiptap-content :deep(.ProseMirror a) {
  color: #4f8ef7;
  text-decoration: underline;
}

.ccs-tiptap-content :deep(.ProseMirror img) {
  max-width: 100%;
  border-radius: 6px;
  margin: 6px 0;
}

.ccs-remove-btn {
  border: 1px solid #7f1d1d;
  background: transparent;
  color: #fca5a5;
  border-radius: 7px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  font-weight: 600;
  flex-shrink: 0;
  margin-top: 6px;
}

.ccs-remove-btn:hover {
  background: #7f1d1d22;
}

.ccs-add-btn {
  align-self: flex-start;
  border: 1px dashed #1e3352;
  background: transparent;
  color: #4f8ef7;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  cursor: pointer;
  font-weight: 600;
  transition: background 0.1s, border-color 0.1s;
}

.ccs-add-btn:hover {
  background: #0f1c2e;
  border-color: #4f8ef7;
}
</style>
